// Accessible loading skeletons for lazily loaded Thinking Academy sections.
// Hook-free, pure presentation — safe to render inside Suspense fallbacks
// without delaying hydration or tripping tree-shaking.
import { Skeleton } from "@/components/ui/skeleton";

type Props = { label?: string };

/**
 * Full-page skeleton used while a Thinking Academy route chunk loads.
 * Mirrors the shared TiaPageShell layout (back link, header, stats, grid)
 * so navigation stays visually stable.
 */
export function TiaPageSkeleton({ label = "Loading Thinking Academy" }: Props) {
  return (
    <section
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      className="container mx-auto px-4 py-6 max-w-5xl animate-in fade-in duration-200"
    >
      <span className="sr-only">{label}…</span>

      {/* Back link */}
      <Skeleton className="h-8 w-32 mb-6" />

      {/* Header */}
      <div className="text-center space-y-3 mb-8">
        <Skeleton className="h-9 w-72 mx-auto" />
        <Skeleton className="h-4 w-full max-w-xl mx-auto" />
        <Skeleton className="h-4 w-2/3 max-w-md mx-auto" />
        <div className="flex justify-center gap-2 pt-1">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-32 rounded-full" />
          <Skeleton className="h-6 w-28 rounded-full" />
        </div>
      </div>

      {/* Stats card */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-5 rounded-lg border border-border/40">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
        ))}
      </div>

      {/* Content grid */}
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border/40 p-4 space-y-3">
            <Skeleton className="h-5 w-48" />
            {Array.from({ length: 3 }).map((__, j) => (
              <Skeleton key={j} className="h-8 w-full" />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Compact inline skeleton for lazy panels (e.g. AI Coach virtualized list)
 * mounted inside an already-rendered page.
 */
export function TiaPanelSkeleton({ label = "Loading panel" }: Props) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      className="space-y-2 p-2"
    >
      <span className="sr-only">{label}…</span>
      <Skeleton className="h-12 w-3/4" />
      <Skeleton className="h-12 w-1/2 ml-auto" />
      <Skeleton className="h-12 w-2/3" />
    </div>
  );
}
